import { blobSource } from '../core/bytes';
import { readChapters, type Chapter } from '../core/chapters';
import { readMovie } from '../core/mp4-read';
import { formatDuration } from '../core/format';
import type { LibraryFile } from './library';

export interface SingleDom {
  readonly title: HTMLElement;
  readonly meta: HTMLElement;
  readonly video: HTMLVideoElement;
  readonly chapters: HTMLOListElement;
  readonly videos: HTMLOListElement;
  readonly merge: HTMLButtonElement;
}

const fileName = (file: LibraryFile): string => file.path.slice(file.path.lastIndexOf('/') + 1);

const clockOf = (seconds: number): string => {
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

export class SingleVideoView {
  private url: string | undefined;
  private chapters: readonly Chapter[] = [];
  private readonly highlight = (): void => this.markCurrent();
  private readonly mergeClick = (): void => this.onMerge(this.files);

  constructor(
    private readonly dom: SingleDom,
    private readonly files: readonly LibraryFile[],
    private readonly onMerge: (files: readonly LibraryFile[]) => void,
    private readonly onError: (message: string) => void,
  ) {
    dom.video.addEventListener('timeupdate', this.highlight);
    dom.merge.addEventListener('click', this.mergeClick);
    dom.merge.hidden = files.length < 2;
    dom.videos.hidden = files.length < 2;
    dom.videos.replaceChildren(...files.map((file) => this.videoButton(file)));
    void this.open(files[0]!);
  }

  async open(file: LibraryFile): Promise<void> {
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = URL.createObjectURL(file.file);
    this.dom.video.src = this.url;
    this.dom.title.textContent = fileName(file);
    this.dom.videos.querySelectorAll('button').forEach((button) => button.toggleAttribute('aria-current', button.dataset['path'] === file.path));
    try {
      const source = blobSource(file.file);
      const movie = await readMovie(source);
      this.chapters = await readChapters(source, movie);
      this.dom.meta.textContent = `${formatDuration(movie.duration)} · ${this.chapters.length ? `${this.chapters.length} chapters` : 'no chapters'}`;
      this.renderChapters();
    } catch (error) {
      this.chapters = [];
      this.dom.chapters.replaceChildren();
      this.dom.meta.textContent = '';
      this.onError(`Could not read the chapters of ${fileName(file)}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  destroy(): void {
    this.dom.video.removeEventListener('timeupdate', this.highlight);
    this.dom.merge.removeEventListener('click', this.mergeClick);
    this.dom.video.pause();
    this.dom.video.removeAttribute('src');
    this.dom.video.load();
    if (this.url) URL.revokeObjectURL(this.url);
    this.dom.chapters.replaceChildren();
    this.dom.videos.replaceChildren();
  }

  private videoButton(file: LibraryFile): HTMLLIElement {
    const item = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tv-chapter';
    button.dataset['path'] = file.path;
    button.textContent = fileName(file);
    button.addEventListener('click', () => void this.open(file));
    item.append(button);
    return item;
  }

  private renderChapters(): void {
    this.dom.chapters.replaceChildren(
      ...this.chapters.map((chapter, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tv-chapter';
        button.dataset['index'] = String(index);
        const time = document.createElement('span');
        time.className = 'tv-chapter-time';
        time.textContent = clockOf(chapter.start);
        button.append(time, ` ${chapter.title}`);
        button.addEventListener('click', () => {
          this.dom.video.currentTime = chapter.start;
          void this.dom.video.play().catch(() => undefined);
        });
        item.append(button);
        return item;
      }),
    );
    this.markCurrent();
  }

  private markCurrent(): void {
    const time = this.dom.video.currentTime;
    const current = this.chapters.findLastIndex((chapter) => chapter.start <= time + 0.05);
    this.dom.chapters.querySelectorAll<HTMLElement>('[data-index]').forEach((button) => button.toggleAttribute('aria-current', Number(button.dataset['index']) === current));
  }
}
